import React from 'react';
import { Order } from '../../types/order';
import Modal from '../common/Modal';
import DocumentChain from '../common/DocumentChain';
import { 
  ShoppingCartIcon,
  CalendarIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  CubeIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';

interface OrderViewProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'production': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'ready': return 'bg-green-100 text-green-800 border-green-200';
    case 'shipped': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'delivered': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft': return 'Tervezet';
    case 'pending': return 'Függőben';
    case 'confirmed': return 'Megerősítve';
    case 'production': return 'Gyártás alatt';
    case 'ready': return 'Kész';
    case 'shipped': return 'Szállítva';
    case 'delivered': return 'Kiszállítva';
    case 'cancelled': return 'Törölve';
    default: return status;
  }
};

const OrderView: React.FC<OrderViewProps> = ({ order, isOpen, onClose }) => {
  const { t } = useLanguage();

  if (!order) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Megrendelés részletei - ${order.number}`}
      size="6xl"
    >
      <div className="space-y-6 p-6 max-h-[80vh] overflow-y-auto">
        {/* Fejléc információk */}
        <div className="crm-card">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <ShoppingCartIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1">
              <div className="flex flex-wrap items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {order.number}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Létrehozva: {new Date(order.createdAt).toLocaleDateString('hu-HU')}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
                  {getStatusLabel(order.status)}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <CalendarIcon className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Megrendelés dátuma:</span>
                  <span>{new Date(order.orderDate).toLocaleDateString('hu-HU')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">Összeg:</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {formatCurrency(order.totalAmount, order.currency)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bizonylat lánc */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <LinkIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Bizonylat lánc</h3>
          </div>
          <div className="p-4">
              <DocumentChain
                documentId={order.id}
                documentType="order"
                className="text-sm"
                compact={true}
              />
            </div>
          </div>
        </div>

        {/* Ügyfél információk */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Ügyfél információk</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Név:</span>
                  <span className="ml-2">{order.customerName}</span>
                </div>
                {order.customerEmail && (
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                    <span className="ml-2">{order.customerEmail}</span>
                  </div>
                )}
                {order.customerPhone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Telefon:</span>
                    <span className="ml-2">{order.customerPhone}</span>
                  </div>
                )}
                {order.customerTaxNumber && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Adószám:</span>
                    <span className="ml-2">{order.customerTaxNumber}</span>
                  </div>
                )}
                <div className="md:col-span-2 flex items-start">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mr-1 mt-0.5" />
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Cím:</span>
                    <div className="ml-2 whitespace-pre-line">{order.customerAddress}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Szállítási információk */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TruckIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítási információk</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {order.requestedDeliveryDate && (
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Kért dátum:</span>
                    <span className="ml-2">{new Date(order.requestedDeliveryDate).toLocaleDateString('hu-HU')}</span>
                  </div>
                )}
                {order.confirmedDeliveryDate && (
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 text-green-500 mr-1" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Megerősített dátum:</span>
                    <span className="ml-2 text-green-600 dark:text-green-400 font-medium">
                      {new Date(order.confirmedDeliveryDate).toLocaleDateString('hu-HU')}
                    </span>
                  </div>
                )}
                {order.deliveryAddress && (
                  <div className="md:col-span-2 flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-500 mr-1 mt-0.5" />
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Szállítási cím:</span>
                      <div className="ml-2 whitespace-pre-line">{order.deliveryAddress}</div>
                    </div>
                  </div>
                )}
                {order.deliveryInstructions && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Szállítási utasítások:</span>
                    <div className="ml-2 mt-1 whitespace-pre-line text-gray-600 dark:text-gray-400">
                      {order.deliveryInstructions}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Megrendelési tételek */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CubeIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Megrendelési tételek</h3>
          </div>
          <div className="p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Termék
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Mennyiség
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Egységár
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Összeg
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {order.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {item.productName}
                            </div>
                            {item.productCode && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Kód: {item.productCode}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900 dark:text-gray-100">
                          {item.quantity} db
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(item.unitPrice, item.currency)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(item.lineTotal, item.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        Nettó összeg:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(order.subtotal, order.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        ÁFA:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(order.taxAmount, order.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Bruttó összeg:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {formatCurrency(order.totalAmount, order.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Fizetési adatok */}
        {(order.paymentTerms || order.paymentMethod) && (
          <div className="crm-card">
            <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Fizetési információk</h3>
          </div>
          <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {order.paymentTerms && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Fizetési feltételek:</span>
                      <span className="ml-2">{order.paymentTerms}</span>
                    </div>
                  )}
                  {order.paymentMethod && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Fizetési mód:</span>
                      <span className="ml-2">{order.paymentMethod}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Megjegyzések */}
        {order.notes && (
          <div className="crm-card">
            <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Megjegyzések</h3>
          </div>
          <div className="p-4">
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {order.notes}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Szervezet információk */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Eladó információk</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Cég neve:</span>
                  <span className="ml-2">{order.organizationName}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Adószám:</span>
                  <span className="ml-2">{order.organizationTaxNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Cégjegyzékszám:</span>
                  <span className="ml-2">{order.organizationRegistrationNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Képviselő:</span>
                  <span className="ml-2">{order.representativeName}</span>
                </div>
                <div className="md:col-span-2 flex items-start">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mr-1 mt-0.5" />
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Cím:</span>
                    <div className="ml-2 whitespace-pre-line">{order.organizationAddress}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bezárás gomb */}
        <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderView; 